import { GraphQLResult } from "@aws-amplify/api-graphql";
import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import { config } from "src/config";
import { DeepPartial } from "src/types/utils";

import { Mutation, Query, TypedDocumentString } from "../generated/graphql";
import { extractGraphqlOperationName, GraphQLRequestError, NormalizedGraphQLError } from "./utils";

type GraphQLOptions = {
    authMode?: "AMAZON_COGNITO_USER_POOLS" | "API_KEY" | "AWS_IAM";
};

/**
 * AppSync puts the error type at the TOP LEVEL of each entry in `errors[]`
 * rather than under `extensions`, and the `GraphQLError` type from the graphql
 * package does not model that — hence the cast at the call site. Reading only
 * `extensions` silently dropped every `util.error(msg, type)` a resolver
 * raised, which is how TTV2-1659 hid a questionnaire-limit rejection behind a
 * generic "save failed" for months. The `extensions` lookups are kept below as
 * a fallback for spec-compliant endpoints; against AppSync they never match.
 *
 * Note that `util.error`'s third argument does NOT reach the client. AppSync
 * fills `errors[].data` with the selection-set shape (`{ "id": null }`) and
 * keeps the resolver's payload in CloudWatch alone, so anything a message needs
 * to name — a limit, a count — has to come from client-side state.
 */
type AppSyncGraphQLError = {
    errorType?: string;
};

export const graphqlFetch = async <
    TResult extends DeepPartial<Query> | DeepPartial<Mutation>,
    TVariables extends Record<string, unknown>,
>(
    query: TypedDocumentString<TResult, TVariables>,
    variables?: TVariables extends Record<string, unknown> ? TVariables : never,
    options?: GraphQLOptions
): Promise<NonNullable<GraphQLResult<TResult>["data"]>> => {
    const queryString = query.toString();
    const { name: operationName, type: operationType } = extractGraphqlOperationName(queryString) || {};

    Sentry.addBreadcrumb({
        category: "graphql",
        level: "info",
        message: `GraphQL ${operationName || "operation"} start`,
        data: {
            operationName,
            variablesKeys: Object.keys((variables as Record<string, unknown>) || {}).sort(),
        },
    });

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (!options?.authMode || options?.authMode === "AMAZON_COGNITO_USER_POOLS") {
        const session = await Auth.currentSession();
        const token = session?.getIdToken()?.getJwtToken();
        headers.Authorization = `Bearer ${token}`;
    } else if (options?.authMode === "API_KEY") {
        headers["x-api-key"] = config.api.key;
    }

    const response = await fetch(config.api.endpointUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: queryString, variables }),
    });

    const requestId = response.headers.get("x-amzn-requestid");

    if (!response.ok) {
        throw new GraphQLRequestError({
            message: `GraphQL request failed: ${response.status} ${response.statusText}`,
            operationType,
            operationName,
            normalized: [],
            variables,
            requestId,
        });
    }

    const responseData = (await response.json()) as GraphQLResult<TResult>;

    if (responseData.errors?.length) {
        const normalized: NormalizedGraphQLError[] = responseData.errors.map((e) => ({
            message: e?.message || "Unknown GraphQL error",
            path: Array.isArray(e?.path) ? e.path.join(".") : e?.path?.toString(),
            errorType: (e as AppSyncGraphQLError)?.errorType || e?.extensions?.errorType || e?.extensions?.code,
            code: e?.extensions?.code,
            source: e?.source?.body,
        }));

        const message = normalized
            .map((e) => e.message)
            .filter(Boolean)
            .join("; ");
        const gqlError = new GraphQLRequestError<TVariables>({
            message,
            operationType,
            operationName,
            normalized,
            variables: variables as TVariables,
            requestId,
        });

        Sentry.addBreadcrumb({
            category: "graphql",
            level: "error",
            message: `GraphQL ${operationName || "operation"} failed`,
            data: {
                operationName,
                errorTypes: Array.from(new Set(normalized.map((n) => n.errorType)))
                    .filter(Boolean)
                    .join(", "),
                paths: Array.from(new Set(normalized.map((n) => n.path)))
                    .filter(Boolean)
                    .join(", "),
                requestId,
            },
        });
        throw gqlError;
    }

    if (!responseData.data) {
        const noDataError = new GraphQLRequestError<TVariables>({
            message: "GraphQL: no data returned",
            operationType,
            operationName,
            normalized: [],
            variables: variables as TVariables,
            requestId,
        });
        throw noDataError;
    }

    Sentry.addBreadcrumb({
        category: "graphql",
        level: "info",
        message: `GraphQL ${operationName || "operation"} success`,
        data: { operationName, requestId },
    });
    return responseData.data;
};
